/* @jsx h */
import { defineComponent, h, ref, computed, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useTrpc } from "@app/vue/providers/index";
import type { RestaurantCustomer } from "@lemmonhq/types";

export const CustomerPicker = defineComponent<{
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}>({
  name: "CustomerPicker",
  props: ["selectedIds", "onChange"],
  setup(props) {
    const trpc = useTrpc();
    const searchInput = ref("");
    const searchQuery = ref("");
    const showDropdown = ref(false);

    let debounceTimer: ReturnType<typeof setTimeout>;
    watch(searchInput, (v) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => { searchQuery.value = v; }, 300);
    });

    const customersQuery = useQuery({
      queryKey: computed(() => ["customers", "search", searchQuery.value]),
      queryFn: () => trpc.reservationCustomers.list.query({ search: searchQuery.value || undefined, limit: 20 }),
      staleTime: 30_000,
      enabled: computed(() => showDropdown.value),
    });

    const results = computed<RestaurantCustomer[]>(
      () => (customersQuery.data?.value?.data as RestaurantCustomer[]) ?? [],
    );

    const selectedSet = computed(() => new Set(props.selectedIds));

    // Cache customer objects so chips stay after search term changes
    const cache = ref<Map<string, RestaurantCustomer>>(new Map());
    watch(results, (list) => {
      list.forEach((c) => cache.value.set(c.id, c));
    });

    const selectedCustomers = computed<RestaurantCustomer[]>(() =>
      props.selectedIds.map((id) => cache.value.get(id)).filter(Boolean) as RestaurantCustomer[],
    );

    const toggle = (customer: RestaurantCustomer) => {
      cache.value.set(customer.id, customer);
      const ids = new Set(props.selectedIds);
      ids.has(customer.id) ? ids.delete(customer.id) : ids.add(customer.id);
      props.onChange([...ids]);
    };

    // Delay closing so mousedown on a result row fires before blur closes the dropdown
    const onBlur = () => setTimeout(() => { showDropdown.value = false; }, 150);

    return () => (
      <div class="space-y-2">
        {/* Search */}
        <div class="relative">
          <div class={`flex items-center gap-2 input input-bordered input-sm px-3 cursor-text ${showDropdown.value ? "input-primary" : ""}`}>
            <span class="material-symbols-outlined text-sm text-base-content/40 shrink-0">search</span>
            <input
              type="text"
              value={searchInput.value}
              onInput={(e) => (searchInput.value = (e.target as HTMLInputElement).value)}
              onFocus={() => (showDropdown.value = true)}
              onBlur={onBlur}
              class="flex-1 bg-transparent outline-none text-sm min-w-0"
              placeholder="Search by name, email or phone…"
            />
            {searchInput.value && (
              <button type="button" onMousedown={(e) => e.preventDefault()} onClick={() => { searchInput.value = ""; searchQuery.value = ""; }} class="text-base-content/40 hover:text-base-content">
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            )}
          </div>

          {showDropdown.value && (
            <div class="absolute z-30 w-full mt-1 bg-base-200 border border-base-content/10 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto">
              {customersQuery.isLoading.value ? (
                <div class="flex items-center justify-center gap-2 py-6 text-base-content/40 text-sm">
                  <span class="loading loading-spinner loading-xs" /> Searching…
                </div>
              ) : results.value.length === 0 ? (
                <div class="py-6 text-center text-sm text-base-content/40">
                  {searchQuery.value ? "No customers found" : "Start typing to search"}
                </div>
              ) : (
                <div class="divide-y divide-base-content/5">
                  {results.value.map((c) => {
                    const selected = selectedSet.value.has(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onMousedown={(e) => e.preventDefault()}
                        onClick={() => toggle(c)}
                        class={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${selected ? "bg-primary/10" : "hover:bg-base-content/5"}`}
                      >
                        <div class={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold uppercase ${selected ? "bg-primary/20 text-primary" : "bg-base-300 text-base-content/50"}`}>
                          {(c.name ?? "?")[0]}
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="text-sm font-medium text-accent truncate">{c.name ?? "—"}</div>
                          <div class="text-xs text-base-content/50 truncate">{[c.email, c.phone].filter(Boolean).join(" · ") || "No contact"}</div>
                        </div>
                        <div class={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${selected ? "bg-primary text-primary-content" : "border-2 border-base-content/20"}`}>
                          {selected && <span class="material-symbols-outlined text-xs">check</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected list */}
        {selectedCustomers.value.length > 0 && (
          <div class="border border-base-content/10 rounded-xl overflow-hidden">
            <div class="overflow-y-auto" style="max-height: 250px">
              {selectedCustomers.value.map((c, i) => (
                <div
                  key={c.id}
                  class={`flex items-center gap-2.5 px-3 py-2 ${i < selectedCustomers.value.length - 1 ? "border-b border-base-content/5" : ""}`}
                >
                  <div class="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center shrink-0 text-xs font-semibold uppercase text-primary">
                    {(c.name ?? "?")[0]}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-accent truncate">{c.name ?? "—"}</div>
                    <div class="text-xs text-base-content/40 truncate">{c.email ?? c.phone ?? ""}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => props.onChange(props.selectedIds.filter((id) => id !== c.id))}
                    class="shrink-0 text-base-content/30 hover:text-error transition-colors"
                  >
                    <span class="material-symbols-outlined text-base">close</span>
                  </button>
                </div>
              ))}
            </div>
            <div class="px-3 py-1.5 bg-base-300/30 border-t border-base-content/5 text-xs text-base-content/40">
              {props.selectedIds.length} customer{props.selectedIds.length !== 1 ? "s" : ""} selected
            </div>
          </div>
        )}
      </div>
    );
  },
});
